// Module 19 (BL-380) G2 - the guest CSV: read a file, check every row the same way the database does, show what would happen.
// No network. The database re-checks everything inside m19_import_guests() and is the final word.
import { toE164 } from '../../../lib/phone';
import { DEFAULT_COUNTRY, IMPORT_MAX_ROWS, PLUS_ONES_MAX, guestName, normalizeName } from './guestConstants';
import { RSVP_STATUSES } from '../eventLabels';
import { DEFAULT_EVENT_SETTINGS, plusOnesDefault } from '../eventSettings';

export const TEMPLATE_HEADERS = ['First name', 'Last name', 'Email', 'Phone', 'Group', 'RSVP', 'Plus-ones allowed', 'Notes'];
const TEMPLATE_EXAMPLES = [
    ['Grace', 'Hopper', 'grace@example.com', '+1 561 409 9456', 'Family', 'No reply', '1', 'Window seat'],
    ['Alan', 'Turing', 'alan@example.com', '', 'Friends', 'Yes', '0', ''],
];
// G6: two more columns while the shuttle question is on (Events Settings > Hotel & Travel).
export const SHUTTLE_HEADERS = ['Needs shuttle', 'Arrival'];
const SHUTTLE_EXAMPLES = [['Yes', 'DL 1234, lands ATL 3:05 PM'], ['No', '']];

export function templateCsv(settings = DEFAULT_EVENT_SETTINGS) {
    const shuttle = settings.shuttle_enabled !== false;
    const headers = shuttle ? [...TEMPLATE_HEADERS, ...SHUTTLE_HEADERS] : TEMPLATE_HEADERS;
    const examples = TEMPLATE_EXAMPLES.map((row, index) => (shuttle ? [...row, ...SHUTTLE_EXAMPLES[index]] : row));
    return [headers, ...examples].map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

function csvCell(value) {
    const text = String(value ?? '');
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// A small, forgiving CSV reader: BOM, CRLF / LF / CR, quoted cells with commas, quotes and line breaks, comma / semicolon / tab.
export function parseCsv(input) {
    let text = String(input ?? '').replace(/^﻿/, '');
    const firstLine = text.split(/\r\n|\n|\r/, 1)[0] || '';
    const delimiter = [',', ';', '\t'].map((d) => [d, firstLine.split(d).length]).sort((a, b) => b[1] - a[1])[0][0];
    const rows = [];
    let row = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];
        if (quoted) {
            if (char === '"' && text[i + 1] === '"') { cell += '"'; i += 1; }
            else if (char === '"') quoted = false;
            else cell += char;
        } else if (char === '"') {
            quoted = true;
        } else if (char === delimiter) {
            row.push(cell); cell = '';
        } else if (char === '\n' || char === '\r') {
            if (char === '\r' && text[i + 1] === '\n') i += 1;
            row.push(cell); cell = '';
            rows.push(row); row = [];
        } else {
            cell += char;
        }
    }
    if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
    return rows;
}

const key = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const ALIASES = {
    first_name: ['firstname', 'first', 'givenname', 'forename'],
    last_name: ['lastname', 'last', 'surname', 'familyname'],
    full_name: ['name', 'fullname', 'guest', 'guestname'],
    email: ['email', 'emailaddress', 'mail'],
    phone: ['phone', 'phonenumber', 'mobile', 'mobilephone', 'cell', 'cellphone', 'tel', 'telephone'],
    group: ['group', 'groupname', 'side'],
    rsvp_status: ['rsvp', 'rsvpstatus', 'status', 'response'],
    plus_ones_allowed: ['plusones', 'plusonesallowed', 'plusone', 'plusoneallowed'],
    notes: ['notes', 'note', 'comments', 'comment'],
    needs_shuttle: ['needsshuttle', 'needshuttle', 'shuttle', 'needsaride', 'airportshuttle'],
    arrival_info: ['arrival', 'arrivalinfo', 'arrivaldetails', 'arrivaltime', 'flight', 'flightinfo'],
};

// G6: "Needs shuttle" accepts Yes / No in the usual spellings; blank = No. The database reads the same words.
const SHUTTLE_YES = ['yes', 'y', 'true', 't', '1', 'x'];
const SHUTTLE_NO = ['', 'no', 'n', 'false', 'f', '0'];
export function shuttleFromText(text) {
    const wanted = String(text ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (SHUTTLE_YES.includes(wanted)) return true;
    if (SHUTTLE_NO.includes(wanted)) return false;
    return null;
}

// DC-1: the screen says Yes / No / No reply / Maybe. A file may use those words OR the older Accepted / Declined / Pending (and the others below).
// Spaces, capitals and punctuation do not matter ("No reply", "no-reply" and "NOREPLY" are the same). The stored values never change.
const RSVP_WORDS = {
    pending: ['', 'pending', 'invited', 'noreply', 'awaiting', 'notreplied'],
    accepted: ['accepted', 'accept', 'yes', 'going', 'attending', 'confirmed'],
    declined: ['declined', 'decline', 'no', 'notgoing', 'cantmakeit', 'notattending'],
    maybe: ['maybe', 'tentative', 'unsure', 'perhaps'],
};
export function rsvpFromText(text) {
    const wanted = key(text);
    return RSVP_STATUSES.find((status) => RSVP_WORDS[status].includes(wanted)) || null;
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const trim = (value) => String(value ?? '').trim();

// Reads the CSV text. `existing` is the guest list already on the event (to spot people who are already on it).
// Returns { rows, problems, counts }. Each row: { line, values, errors:[{field,message}], duplicate:null|string, shared:[string], state:'ready'|'invalid'|'duplicate' }.
// A duplicate is the same normalized full name AND the same email or phone (earlier in the file, or already on the event): skipped, not an error.
// Different names sharing a phone or an email are imported normally; `shared` carries the non-blocking notes shown in the preview.
// G8: `settings` are the organization's Events Settings. With "Maybe" off, a Maybe RSVP in the file is a row problem (existing guests keep
// their status). With plus-ones off, a plus-ones number above 0 is a row problem. A blank plus-ones cell takes the saved default.
export function readGuestCsv(text, { existing = [], defaultCountry = DEFAULT_COUNTRY, settings = DEFAULT_EVENT_SETTINGS } = {}) {
    const table = parseCsv(text).filter((cells) => cells.some((cell) => trim(cell) !== ''));
    const empty = { rows: [], problems: [], counts: { total: 0, ready: 0, duplicates: 0, invalid: 0, shared: 0 } };
    if (!table.length) return { ...empty, problems: ['The file is empty. Download the template to see the columns.'] };

    const header = table[0].map(key);
    const columnOf = (field) => header.findIndex((name) => ALIASES[field].includes(name));
    const cols = Object.fromEntries(Object.keys(ALIASES).map((field) => [field, columnOf(field)]));
    if (cols.first_name < 0 && cols.full_name < 0) {
        return { ...empty, problems: ['We could not find a "First name" column. Download the template to see the columns we read.'] };
    }
    const data = table.slice(1);
    if (!data.length) return { ...empty, problems: ['The file has column headings but no guests in it.'] };
    if (data.length > IMPORT_MAX_ROWS) {
        return { ...empty, counts: { ...empty.counts, total: data.length }, problems: [`This file has ${data.length.toLocaleString('en-US')} guests. You can import up to ${IMPORT_MAX_ROWS.toLocaleString('en-US')} at a time, so split it into smaller files.`] };
    }

    // who is already known: key "normalized name|value" for duplicates, and value -> display name for shared-contact notes
    const seenEmailKeys = new Set();
    const seenPhoneKeys = new Set();
    const emailOwners = new Map();   // email -> [display names]
    const phoneOwners = new Map();
    const remember = (name, display, email, phone) => {
        if (email) { seenEmailKeys.add(`${name}|${email}`); emailOwners.set(email, [...(emailOwners.get(email) || []), { name, display }]); }
        if (phone) { seenPhoneKeys.add(`${name}|${phone}`); phoneOwners.set(phone, [...(phoneOwners.get(phone) || []), { name, display }]); }
    };
    existing.forEach((guest) => remember(
        normalizeName(guest.first_name, guest.last_name), guestName(guest), String(guest.email || '').toLowerCase(), guest.phone || '',
    ));
    const fileEmailKeys = new Set();
    const filePhoneKeys = new Set();
    const cell = (cells, field) => (cols[field] >= 0 ? trim(cells[cols[field]]) : '');

    const rows = data.map((cells, index) => {
        const errors = [];
        let first = cell(cells, 'first_name');
        let last = cell(cells, 'last_name');
        if (cols.first_name < 0 && cols.full_name >= 0) {
            const [head, ...rest] = cell(cells, 'full_name').split(/\s+/);
            first = head || '';
            last = rest.join(' ');
        }
        const email = cell(cells, 'email').toLowerCase();
        const phoneText = cell(cells, 'phone');
        const rsvpText = cell(cells, 'rsvp_status');
        const plusText = cell(cells, 'plus_ones_allowed') || String(plusOnesDefault(settings));
        const group = cell(cells, 'group');
        const notes = cell(cells, 'notes');
        // G6: read only while the shuttle question is on; otherwise the columns are ignored (nothing is stored)
        const shuttleOn = settings.shuttle_enabled !== false;
        const shuttleText = shuttleOn ? cell(cells, 'needs_shuttle') : '';
        const arrival = shuttleOn ? cell(cells, 'arrival_info') : '';
        let phone = '';

        if (!first) errors.push({ field: 'first_name', message: 'First name is missing.' });
        else if (first.length > 100) errors.push({ field: 'first_name', message: 'First name is longer than 100 characters.' });
        if (last.length > 100) errors.push({ field: 'last_name', message: 'Last name is longer than 100 characters.' });
        if (email && (email.length > 254 || !EMAIL.test(email))) errors.push({ field: 'email', message: 'That email address does not look right.' });
        if (phoneText) {
            phone = toE164(phoneText, defaultCountry) || '';
            if (!phone) errors.push({ field: 'phone', message: 'That phone number does not look right. Include the country code for numbers outside the US.' });
        }
        const rsvp = rsvpFromText(rsvpText);
        if (!rsvp) errors.push({ field: 'rsvp_status', message: settings.allow_maybe ? 'RSVP must be Yes, No, No reply or Maybe.' : 'RSVP must be Yes, No or No reply.' });
        else if (rsvp === 'maybe' && !settings.allow_maybe) errors.push({ field: 'rsvp_status', message: '“Maybe” is turned off in Events Settings. Use Yes, No or No reply.' });
        if (!/^\d{1,3}$/.test(plusText) || Number(plusText) > PLUS_ONES_MAX) errors.push({ field: 'plus_ones_allowed', message: `Plus-ones allowed must be a number from 0 to ${PLUS_ONES_MAX}.` });
        else if (!settings.allow_plus_ones && Number(plusText) > 0) errors.push({ field: 'plus_ones_allowed', message: 'Plus-ones are turned off in Events Settings. Leave this at 0.' });
        if (group.length > 80) errors.push({ field: 'group', message: 'The group name is longer than 80 characters.' });
        if (notes.length > 2000) errors.push({ field: 'notes', message: 'The notes are longer than 2,000 characters.' });
        const needsShuttle = shuttleFromText(shuttleText);
        if (needsShuttle === null) errors.push({ field: 'needs_shuttle', message: 'Needs shuttle must be Yes or No (or left blank for No).' });
        if (arrival.length > 200) errors.push({ field: 'arrival_info', message: 'The arrival details are longer than 200 characters.' });

        let duplicate = null;
        const shared = [];
        if (!errors.length) {
            const name = normalizeName(first, last);
            const known = (set, value) => value && set.has(`${name}|${value}`);
            // earlier rows of this file are also remembered in seen*Keys (with the guests already on the event), so the file's own sets are asked first
            if (email && fileEmailKeys.has(`${name}|${email}`)) duplicate = 'The same guest (same name and email address) appears earlier in this file.';
            else if (phone && filePhoneKeys.has(`${name}|${phone}`)) duplicate = 'The same guest (same name and phone number) appears earlier in this file.';
            else if (known(seenEmailKeys, email)) duplicate = 'This guest (same name and email address) is already on the guest list.';
            else if (known(seenPhoneKeys, phone)) duplicate = 'This guest (same name and phone number) is already on the guest list.';
            if (!duplicate) {
                const display = [first, last].filter(Boolean).join(' ');
                const otherEmail = email && (emailOwners.get(email) || []).find((owner) => owner.name !== name);
                const otherPhone = phone && (phoneOwners.get(phone) || []).find((owner) => owner.name !== name);
                if (otherEmail) shared.push(`Shares an email address with ${otherEmail.display}.`);
                if (otherPhone) shared.push(`Shares a phone number with ${otherPhone.display}.`);
                remember(name, display, email, phone);
                if (email) fileEmailKeys.add(`${name}|${email}`);
                if (phone) filePhoneKeys.add(`${name}|${phone}`);
            }
        }
        return {
            line: index + 2, // the line in the file (line 1 is the headings)
            values: {
                first_name: first, last_name: last, email, phone, group, rsvp_status: rsvp || rsvpText, plus_ones_allowed: plusText, notes,
                ...(shuttleOn ? { needs_shuttle: needsShuttle ? 'yes' : 'no', arrival_info: arrival } : {}),
            },
            errors,
            duplicate,
            shared,
            state: errors.length ? 'invalid' : duplicate ? 'duplicate' : 'ready',
        };
    });

    const counts = {
        total: rows.length,
        ready: rows.filter((row) => row.state === 'ready').length,
        duplicates: rows.filter((row) => row.state === 'duplicate').length,
        invalid: rows.filter((row) => row.state === 'invalid').length,
        shared: rows.filter((row) => row.state === 'ready' && row.shared.length).length,
    };
    return { rows, problems: [], counts };
}

// What is sent to the database: only the rows that are ready. `lines[i]` is the file line of payload row i + 1,
// so a database message about "row 3" can be shown against the right line of the file.
export function importPayload(rows) {
    const ready = rows.filter((row) => row.state === 'ready');
    return {
        payload: ready.map((row) => ({ ...row.values, plus_ones_allowed: String(row.values.plus_ones_allowed || '0') })),
        lines: ready.map((row) => row.line),
    };
}
